import React, { useEffect, useRef, useState, useMemo } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import { defaults as defaultControls, ScaleLine } from 'ol/control';
import { fromLonLat } from 'ol/proj';
import Overlay from 'ol/Overlay';
import OverlayPopup from './OverlayPopup';

import { useSetAtom } from 'jotai';
import { mapInstanceAtom, mapControlsAtom } from '../../store/atoms/map';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Cluster from 'ol/source/Cluster';
import CircleStyle from 'ol/style/Circle';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import Style from 'ol/style/Style';
import Icon from 'ol/style/Icon';
import Text from 'ol/style/Text';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import { XYZ } from 'ol/source';
import ImageLayer from 'ol/layer/Image';
import ImageStatic from 'ol/source/ImageStatic';
import VectorImageLayer from "ol/layer/VectorImage";

// --- Model
import { StationItem } from '../../models/types/station';

const MapOpenLayers: React.FC = () => {
    const [overlayInfo, setOverlayInfo] = useState<{
        coordinate: [number, number] | any;
        station: any;
    } | null>(null);
    const overlayRef = useRef<HTMLDivElement | null>(null);
    const olOverlay = useRef<Overlay | null>(null);
    const mapRef = useRef<HTMLDivElement | null>(null);
    const mapInstance = useRef<Map | null>(null);
    const setMapInstance = useSetAtom(mapInstanceAtom);
    const setMapControls = useSetAtom(mapControlsAtom);

    const setCenter = (center: [number, number], zoom: number = 12) => {
        mapInstance.current?.getView().animate({
            zoom: zoom,
            center: fromLonLat(center),
            duration: 2000,
        });
    };

    const setZoom = (zoom: number) => {
        mapInstance.current?.getView().animate({
            zoom: zoom,
            duration: 2000,
        });
    };

    const pinMarker = (coords: [number, number], className: string = 'marker-layer') => {
        if (!mapInstance.current) return;
        // Tìm vector layer chứa marker, nếu chưa có thì tạo mới
        let markerLayer = mapInstance.current.getLayers().getArray().find(
            (layer: any) => layer.get('name') === className
        ) as VectorLayer | undefined;

        if (!markerLayer) {
            markerLayer = new VectorLayer({
                source: new VectorSource(),
                className: className,
            });
            mapInstance.current.addLayer(markerLayer);
        }
        // Đảm bảo markerLayer đúng kiểu VectorLayer
        if (markerLayer && typeof markerLayer.getSource === 'function') {
            // Tạo feature mới
            const marker = new Feature({
                geometry: new Point(fromLonLat(coords)),
            });
            marker.setStyle(
                new Style({
                    image: new Icon({
                        src: '/assets/images/red-marker.png',
                        anchor: [0.5, 1],
                        anchorXUnits: 'fraction',
                        anchorYUnits: 'fraction',
                        scale: 1,
                    }),
                })
            );
            const source = markerLayer.getSource();
            if (source) {
                source.addFeature(marker);
            }
        }
    };

    // Cluster source và layer toàn cục
    const clusterVectorSourceRef = useRef<VectorSource | null>(null);
    const clusterSourceRef = useRef<Cluster | null>(null);
    const clusterLayerRef = useRef<VectorLayer | null>(null);

    // Hàm khởi tạo cluster layer (gọi 1 lần trong useEffect khi init map)
    const initClusterLayer = () => {
        if (!mapInstance.current) return;

        const vectorSource = new VectorSource();
        const clusterSource = new Cluster({
            distance: 30, // khoảng cách pixel để gộp
            source: vectorSource,
        });
        const clusterLayer = new VectorLayer({
            source: clusterSource,
            className: 'station-layer',
            style: (feature) => {
                const features = feature.get('features');
                if (features.length > 1) {
                    // Style cho cluster
                    return new Style({
                        image: new CircleStyle({
                            radius: 16,
                            fill: new Fill({ color: '#3399CC' }),
                            stroke: new Stroke({ color: '#fff', width: 2 }),
                        }),
                        text: new Text({
                            text: features.length.toString(),
                            fill: new Fill({ color: '#fff' }),
                            font: 'bold 14px Arial',
                        }),
                    });
                } else {
                    // Style cho marker đơn
                    const station = features[0].getProperties();
                    return new Style({
                        image: new Icon({
                            src: '/assets/images/blue-marker.png',
                            anchor: [0.5, 1],
                            anchorXUnits: 'fraction',
                            anchorYUnits: 'fraction',
                            scale: 0.8,
                        }),
                        text: new Text({
                            text: station.name,
                            font: 'bold 14px Arial',
                            offsetY: 2,
                            fill: new Fill({ color: '#3399CC' }),
                            stroke: new Stroke({ color: '#fff', width: 3 }),
                        }),
                    });
                }
            },
        });
        mapInstance.current.addLayer(clusterLayer);
        clusterVectorSourceRef.current = vectorSource;
        clusterSourceRef.current = clusterSource;
        clusterLayerRef.current = clusterLayer;
    };

    // Thêm tất cả các feature station vào cluster source
    const pinAllStation = (stations: StationItem[]) => {
        if (!clusterVectorSourceRef.current) return;
        const features = stations.map((station) => {
            const marker = new Feature({
                geometry: new Point(fromLonLat([station.longitude, station.latitude])),
                ...station
            });
            return marker;
        });
        clusterVectorSourceRef.current.clear();
        clusterVectorSourceRef.current.addFeatures(features);
    };

    // Thêm 1 station vào cluster vector source
    const pinStation = (station: StationItem) => {
        if (!clusterVectorSourceRef.current) return;
        const marker = new Feature({
            geometry: new Point(fromLonLat([station.longitude, station.latitude])),
            ...station
        });
        clusterVectorSourceRef.current.addFeature(marker);
    };

    const changeBaseLayer = (basemapUrl: string) => {
        if (!mapInstance.current) return;

        const layers = mapInstance.current.getLayers().getArray();
        layers?.forEach((layer: any) => {
            if (layer?.getClassName() === 'base-map') {
                mapInstance.current?.removeLayer(layer);
            }
        });

        const newLayer = new TileLayer({
            source: new XYZ({
                url: basemapUrl,
                maxZoom: 24,
                crossOrigin: "anonymous",
            }),
            className: 'base-map',
        });

        if (newLayer) {
            mapInstance.current.addLayer(newLayer);
        }
    };

    const showStationOverlayAtCoordinate = (coordinate: [number, number], data?: any) => {
        if (!mapInstance.current) return;
        setOverlayInfo({
            coordinate: fromLonLat(coordinate),
            station: [data]
        });

    }

    const handleClickStation = (evt: any) => {
        if (!mapInstance.current) return;
        let found = false;
        mapInstance.current.forEachFeatureAtPixel(evt.pixel, function (feature: any) {
            // Nếu là cluster
            const features = feature.get('features');
            if (features && Array.isArray(features)) {
                // Lấy danh sách các station thực sự
                const stations = features.map(f => {
                    const props = f.getProperties();
                    delete props.geometry;
                    return props; // hoặc f.get('station') nếu bạn set theo cách an toàn
                });
                setOverlayInfo({
                    coordinate: features[0].getGeometry().getCoordinates(),
                    station: stations // hoặc chỉ lấy station đầu tiên nếu muốn
                });
                found = true;
                return true;
            } else {
                // Feature đơn
                const props = feature.getProperties();
                delete props.geometry;
                setOverlayInfo({
                    coordinate: feature.getGeometry().getCoordinates(),
                    station: props // hoặc feature.get('station')
                });
                found = true;
                return true;
            }
        });
        if (!found) {
            setOverlayInfo(null);
        }
    }

    // Hiển thị overlay đúng vị trí marker khi overlayInfo thay đổi
    useEffect(() => {
        if (!mapInstance.current) return;
        if (!overlayInfo || !overlayRef.current) {
            if (olOverlay.current) {
                mapInstance.current.removeOverlay(olOverlay.current);
                olOverlay.current = null;
            }
            return;
        }
        // Nếu đã có overlay thì update lại vị trí
        if (!olOverlay.current) {
            olOverlay.current = new Overlay({
                element: overlayRef.current,
                positioning: 'bottom-center',
                offset: [0, -40],
                stopEvent: true,
            });
            mapInstance.current.addOverlay(olOverlay.current);
        }
        olOverlay.current.setPosition(overlayInfo.coordinate);
    }, [overlayInfo, mapInstance.current]);

    useEffect(() => {
        if (mapRef.current && !mapInstance.current) {
            const baseMapDefault = "https://mt0.google.com/vt/lyrs=m&hl=vi&x={x}&y={y}&z={z}&s=Ga";
            const initialLayer: Array<
                TileLayer | ImageLayer<ImageStatic> | VectorLayer | VectorImageLayer
            > = [];

            const baseMap = new TileLayer({
                source: new XYZ({
                    url: baseMapDefault,
                    maxZoom: 24,
                    crossOrigin: "anonymous",
                }),
                className: 'base-map',
            });

            initialLayer.push(baseMap);

            mapInstance.current = new Map({
                target: mapRef.current,
                layers: initialLayer,
                view: new View({
                    center: fromLonLat([105.390291, 20.566496]),
                    zoom: 7.5,
                }),
                // controls: defaultControls().extend([new ScaleLine({
                //     minWidth: 100,
                //     maxWidth: 200,
                //     units: 'metric',
                // })]),
            });

            // Khởi tạo cluster layer cho station
            initClusterLayer();

            // Đổi con trỏ chuột khi hover vào feature
            mapInstance.current.on('pointermove', function (evt) {
                if (!mapRef.current || !mapInstance.current) return;
                const pixel = mapInstance.current.getEventPixel(evt.originalEvent);
                const hit = mapInstance.current.hasFeatureAtPixel(pixel);
                mapRef.current.style.cursor = hit ? 'pointer' : '';

                // const feature = mapInstance.current?.forEachFeatureAtPixel(pixel!, f => f);
                // if (feature) {
                //     mapRef.current.style.cursor = 'pointer';
                //     const geometry: any = feature.getGeometry();
                //     const coord = geometry.getCoordinates();
                //     const props = feature.getProperties();
                //     // Không lấy các thuộc tính hệ thống nội bộ của ol
                //     delete props.geometry;
                //     setOverlayInfo({
                //         coordinate: coord,
                //         station: props
                //     });
                // } else {
                //     mapRef.current.style.cursor = '';
                //     setOverlayInfo(null);
                // }
            });

            // Thêm sự kiện click để hiển thị overlay khi click vào feature
            mapInstance.current.on('singleclick', function (evt) {
                handleClickStation(evt);
            });

            setMapInstance(mapInstance.current);
            setMapControls({
                setCenter,
                setZoom,
                pinMarker,
                changeBaseLayer,
                pinAllStation,
                showStationOverlayAtCoordinate,
            })

        }

        return () => {
            mapInstance.current?.setTarget(undefined);
            mapInstance.current = null;
        };
    }, []);

    return (
        <div className="relative w-full h-full border border-gray-200">
            <div ref={mapRef} className="w-full h-full min-h-[400px]" />

            <div
                ref={overlayRef}
                style={{ pointerEvents: 'auto', zIndex: 100 }}
            >
                {overlayInfo && (
                    <OverlayPopup
                        coordinate={overlayInfo.coordinate}
                        station={overlayInfo.station}
                        onClose={() => setOverlayInfo(null)}
                    />
                )}
            </div>
        </div>
    );
};

export default MapOpenLayers;